import type {
  User,
  UserUpdateRequest,
  Store,
  StoreWithPriority,
  StoreCreateRequest,
  StoreUpdateRequest,
  StoreProduct,
  RevenueSnapshot,
  RevenueCreateRequest,
  ScheduleGenerateRequest,
  Visit,
  VisitWithStore,
  VisitCreateRequest,
  VisitUpdateRequest,
  DiscountDeal,
  DealCreateRequest,
  DealUpdateRequest,
  PlaceSearchResult,
  DailyRoute,
  ApiResponse,
  AccountWithStores,
  AccountCreateRequest,
  AccountUpdateRequest,
  AccountRevenueSnapshot,
  Market,
  MarketCreateRequest,
  MarketUpdateRequest,
  StoreHealthData,
} from 'shared';

const API_BASE = (import.meta.env.VITE_API_URL ?? '') + '/api';

// Token getter - will be set by the auth hook
let getAuthToken: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  getAuthToken = getter;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    // Include status in message for easy parsing in retry logic
    super(`${status}: ${message}`);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options?.headers as Record<string, string>) || {}),
  };

  // Add auth token if available
  if (getAuthToken) {
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  const data = await response.json() as ApiResponse<T>;

  if (!response.ok || !data.success) {
    throw new ApiError(response.status, data.error || 'Request failed');
  }

  return data.data as T;
}

// User API
export const userApi = {
  get: () => request<User>('/user'),
  update: (data: UserUpdateRequest) =>
    request<User>('/user', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// Store hours sync
export const syncAllStoreHours = () =>
  request<{ total: number; synced: number }>('/stores/sync-all-hours', { method: 'POST' });

// Stores API
export const storesApi = {
  list: (includeInactive = false) =>
    request<StoreWithPriority[]>(`/stores?include_inactive=${includeInactive}`),
  get: (id: string) => request<StoreWithPriority>(`/stores/${id}`),
  create: (data: StoreCreateRequest) =>
    request<Store>('/stores', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: StoreUpdateRequest) =>
    request<Store>(`/stores/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    request<void>(`/stores/${id}`, { method: 'DELETE' }),
  syncHours: (id: string) =>
    request<{ success: boolean }>(`/stores/${id}/sync-hours`, { method: 'POST' }),
  search: (query: string, location?: { lat: number; lng: number }) => {
    let path = `/stores/search?q=${encodeURIComponent(query)}`;
    if (location) {
      path += `&location=${location.lat},${location.lng}`;
    }
    return request<PlaceSearchResult[]>(path);
  },
  products: {
    list: (storeId: string) =>
      request<StoreProduct[]>(`/stores/${storeId}/products`),
    create: (storeId: string, data: { product_name: string; retail_price_cents?: number; notes?: string }) =>
      request<StoreProduct>(`/stores/${storeId}/products`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (storeId: string, productId: string, data: { product_name?: string; retail_price_cents?: number | null; notes?: string | null }) =>
      request<StoreProduct>(`/stores/${storeId}/products/${productId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (storeId: string, productId: string) =>
      request<void>(`/stores/${storeId}/products/${productId}`, { method: 'DELETE' }),
  },
  getPlaceDetails: (placeId: string) =>
    request<{
      place_id: string;
      rating: number | null;
      userRatingCount: number | null;
      reviews: Array<{
        publishTime: string;
        rating: number;
        text?: string;
        authorName?: string;
      }>;
    }>(`/stores/place/${encodeURIComponent(placeId)}`),
};

// Revenue API
export const revenueApi = {
  list: (params?: { storeId?: string; from?: string; to?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.storeId) searchParams.set('store_id', params.storeId);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    return request<RevenueSnapshot[]>(`/revenue?${searchParams}`);
  },
  getYtd: (storeId: string) =>
    request<{ store_id: string; ytd_revenue_cents: number; year_start: string }>(
      `/revenue/ytd/${storeId}`
    ),
  create: (data: RevenueCreateRequest) =>
    request<RevenueSnapshot>('/revenue', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Schedule API
export const scheduleApi = {
  list: (from: string, to: string, status?: string) => {
    let path = `/schedule?from=${from}&to=${to}`;
    if (status) path += `&status=${status}`;
    return request<VisitWithStore[]>(path);
  },
  generate: (data: ScheduleGenerateRequest) =>
    request<{ visits_created: number; start_date: string; end_date: string }>(
      '/schedule/generate',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),
  slotStore: (storeId: string, fromDate: string, notes?: string) =>
    request<{ visit_id: string; scheduled_date: string }>('/schedule/slot-store', {
      method: 'POST',
      body: JSON.stringify({ store_id: storeId, from_date: fromDate, notes }),
    }),
  reorderDay: (date: string, visitIds: string[]) =>
    request<{ updated: number; visits: Array<{ id: string; start: string; end: string }> }>('/schedule/reorder-day', {
      method: 'PUT',
      body: JSON.stringify({ date, visit_ids: visitIds }),
    }),
};

// Visits API
export const visitsApi = {
  get: (id: string) => request<VisitWithStore>(`/visits/${id}`),
  create: (data: VisitCreateRequest) =>
    request<Visit>('/visits', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: VisitUpdateRequest) =>
    request<Visit>(`/visits/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    request<void>(`/visits/${id}`, { method: 'DELETE' }),
};

// Routes API
export const routesApi = {
  getDay: (date: string) => request<DailyRoute>(`/routes/day?date=${date}`),
};

// Accounts API
export const accountsApi = {
  list: () => request<AccountWithStores[]>('/accounts'),
  create: (data: AccountCreateRequest) =>
    request<AccountWithStores>('/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: AccountUpdateRequest) =>
    request<AccountWithStores>(`/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    request<void>(`/accounts/${id}`, { method: 'DELETE' }),
  addRevenue: (id: string, data: { period_start: string; period_end: string; revenue_cents: number }) =>
    request<AccountRevenueSnapshot>(`/accounts/${id}/revenue`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getYtd: (id: string) =>
    request<{ account_id: string; ytd_revenue_cents: number; year_start: string }>(
      `/accounts/${id}/revenue/ytd`
    ),
  getAnnualSummary: (year: number) =>
    request<Record<string, number>>(`/accounts/revenue/annual?year=${year}`),
  getRevenueByPeriod: (from: string, to: string) =>
    request<Record<string, number>>(`/accounts/revenue/period?from=${from}&to=${to}`),
};

// Admin API
interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  created_at: string;
  store_count: number;
  account_count: number;
}

interface ActivityData {
  dates: string[];
  by_user: {
    user_id: string;
    email: string;
    name: string | null;
    total_visits: number;
    daily: number[];
  }[];
  daily_totals: number[];
}

export const adminApi = {
  listUsers: () => request<AdminUser[]>('/admin/users'),
  getActivity: (days = 30) => request<ActivityData>(`/admin/activity?days=${days}`),
};

// Markets API
export const marketsApi = {
  list: () => request<Market[]>('/markets'),
  create: (data: MarketCreateRequest) =>
    request<Market>('/markets', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: MarketUpdateRequest) =>
    request<Market>(`/markets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<void>(`/markets/${id}`, { method: 'DELETE' }),
};

// Health API
export type StoreHealthListItem = StoreHealthData & {
  id: string;
  name: string;
  place_id: string | null;
};

export const healthApi = {
  getStoreHealth: (storeId: string) =>
    request<StoreHealthData | null>(`/stores/${storeId}/health`),
  refreshStoreHealth: (storeId: string) =>
    request<StoreHealthData>(`/stores/${storeId}/health`, { method: 'POST' }),
  listStoreHealth: () =>
    request<StoreHealthListItem[]>('/stores/health'),
  bulkRefreshHealth: () =>
    request<{ refreshed: number; skipped: number; errors: number }>(
      '/stores/health',
      { method: 'POST' }
    ),
};

// Deals API
export const dealsApi = {
  list: (params?: { status?: string; activeOnly?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.activeOnly) searchParams.set('active', 'true');
    return request<DiscountDeal[]>(`/deals?${searchParams}`);
  },
  get: (id: string) => request<DiscountDeal>(`/deals/${id}`),
  create: (data: DealCreateRequest) =>
    request<DiscountDeal>('/deals', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: DealUpdateRequest) =>
    request<DiscountDeal>(`/deals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    request<void>(`/deals/${id}`, { method: 'DELETE' }),
};


// Calendar API
export const calendarApi = {
  status: () => request<{ connected: boolean }>('/calendar/status'),
  disconnect: () => request<void>('/calendar/disconnect', { method: 'DELETE' }),
  getAuthUrl: () => request<{ url: string }>('/calendar/auth').then((d) => d.url),
};
